import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Stethoscope,
  CalendarDays,
  ClipboardList,
  UserRound,
  Mail,
  Megaphone,
  Shield,
  Cable,
  Target,
  Star,
  TrendingUp,
  UserCog,
  Upload,
  Database,
  BookOpen,
  ChevronDown,
  Menu,
  X,
  LogOut,
} from 'lucide-react';
import { useAuth } from '@/hooks';
import type { UserRole } from '@/types';
import styles from './AdminLayout.module.css';

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  roles?: UserRole[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

const NAV_ENTRIES: NavEntry[] = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} /> },
  {
    label: 'Marketing',
    items: [
      { to: '/admin/campaigns', label: 'Campanhas', icon: <Megaphone size={20} /> },
      { to: '/admin/leads', label: 'Leads', icon: <Target size={20} /> },
      { to: '/admin/lead-imports', label: 'Importar Leads', icon: <Upload size={20} />, roles: ['Admin'] },
      { to: '/admin/lead-providers', label: 'Provedores', icon: <Database size={20} />, roles: ['Admin'] },
      { to: '/admin/invitations', label: 'Convites', icon: <Mail size={20} /> },
    ],
  },
  {
    label: 'Pacientes',
    items: [
      { to: '/admin/patients', label: 'Pacientes', icon: <UserRound size={20} /> },
      { to: '/admin/patient-scores', label: 'Score', icon: <Star size={20} /> },
      { to: '/admin/ltv', label: 'LTV', icon: <TrendingUp size={20} /> },
    ],
  },
  {
    label: 'Operacao',
    items: [
      { to: '/admin/schedule', label: 'Agenda', icon: <CalendarDays size={20} /> },
      { to: '/admin/daily-panel', label: 'Painel do Dia', icon: <ClipboardList size={20} /> },
    ],
  },
  {
    label: 'Configuracao',
    items: [
      { to: '/admin/procedure-types', label: 'Procedimentos', icon: <Stethoscope size={20} /> },
      { to: '/admin/resources', label: 'Recursos', icon: <Users size={20} /> },
      { to: '/admin/policies', label: 'Regras', icon: <Shield size={20} />, roles: ['Admin'] },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/admin/users', label: 'Usuarios', icon: <UserCog size={20} />, roles: ['Admin'] },
      { to: '/admin/integrations', label: 'Integracoes', icon: <Cable size={20} />, roles: ['Admin'] },
      { to: '/admin/overview', label: 'Documentacao', icon: <BookOpen size={20} /> },
    ],
  },
];

export function AdminLayout({ children }: AdminLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set<string>());
  const { user, logout, userHasAnyRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const filterItem = useCallback(
    (item: NavItem) => {
      if (!item.roles) return true;
      return userHasAnyRole(item.roles);
    },
    [userHasAnyRole],
  );

  const visibleEntries = useMemo(() => {
    return NAV_ENTRIES.map((entry) => {
      if (!isGroup(entry)) {
        return filterItem(entry) ? entry : null;
      }
      const filteredItems = entry.items.filter(filterItem);
      if (filteredItems.length === 0) return null;
      return { ...entry, items: filteredItems };
    }).filter(Boolean) as NavEntry[];
  }, [filterItem]);

  // Auto-expand the group that contains the active route
  const activeGroup = useMemo(() => {
    for (const entry of NAV_ENTRIES) {
      if (isGroup(entry)) {
        if (entry.items.some((item) => location.pathname.startsWith(item.to))) {
          return entry.label;
        }
      }
    }
    return null;
  }, [location.pathname]);

  const isGroupExpanded = useCallback(
    (label: string) => {
      return expandedGroups.has(label) || activeGroup === label;
    },
    [expandedGroups, activeGroup],
  );

  const toggleGroup = useCallback((label: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const handleOverlayClick = useCallback(() => {
    closeSidebar();
  }, [closeSidebar]);

  const renderNavItem = (item: NavItem) => (
    <li key={item.to}>
      <NavLink
        to={item.to}
        className={({ isActive }) =>
          [styles.navLink, isActive ? styles.navLinkActive : '']
            .filter(Boolean)
            .join(' ')
        }
        onClick={closeSidebar}
      >
        <span className={styles.navIcon} aria-hidden="true">
          {item.icon}
        </span>
        <span>{item.label}</span>
      </NavLink>
    </li>
  );

  const renderGroup = (group: NavGroup) => {
    const expanded = isGroupExpanded(group.label);
    return (
      <li key={group.label}>
        <button
          type="button"
          className={[styles.groupToggle, expanded ? styles.groupToggleExpanded : '']
            .filter(Boolean)
            .join(' ')}
          onClick={() => toggleGroup(group.label)}
          aria-expanded={expanded}
        >
          <span>{group.label}</span>
          <ChevronDown size={16} className={styles.groupChevron} aria-hidden="true" />
        </button>
        {expanded && (
          <ul className={styles.groupItems} role="list">
            {group.items.map(renderNavItem)}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className={styles.layout}>
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className={styles.overlay}
          onClick={handleOverlayClick}
          role="presentation"
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={[styles.sidebar, isSidebarOpen ? styles.sidebarOpen : '']
          .filter(Boolean)
          .join(' ')}
        aria-label="Navegacao principal"
      >
        <div className={styles.sidebarHeader}>
          <span className={styles.logoIcon} aria-hidden="true">+</span>
          <span className={styles.logoText}>
            CRM<span className={styles.logoAccent}>Clinica</span>
          </span>
          <button
            type="button"
            className={styles.closeSidebarButton}
            onClick={closeSidebar}
            aria-label="Fechar menu"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        <nav className={styles.nav} aria-label="Menu administrativo">
          <ul className={styles.navList} role="list">
            {visibleEntries.map((entry) =>
              isGroup(entry) ? renderGroup(entry) : renderNavItem(entry),
            )}
          </ul>
        </nav>
      </aside>

      {/* Main area */}
      <div className={styles.mainArea}>
        {/* Top header */}
        <header className={styles.topHeader}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={toggleSidebar}
            aria-label="Abrir menu"
            aria-expanded={isSidebarOpen}
          >
            <Menu size={24} aria-hidden="true" />
          </button>

          <div className={styles.headerRight}>
            {user && (
              <span className={styles.userName}>{user.fullName}</span>
            )}
            <button
              type="button"
              className={styles.logoutButton}
              onClick={handleLogout}
              aria-label="Sair"
            >
              <LogOut size={18} aria-hidden="true" />
              <span className={styles.logoutText}>Sair</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className={styles.content} id="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
